<template>
    <layout>
        <template v-slot:header>
            <AppHead title="Accounting" />
            <h2>Accounting</h2>
            <flash-messages />
        </template>

        <template v-slot:default>

            <div class="row mb-3">
                <div class="col-lg-3 col-md-6">

                    <div class="row mb-3">
                        <div class="col-12">
                            <card title="Stripe Import">
                                <jet-button @click="importStripePayments()">Import Stripe Payments</jet-button>
                            </card>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-12">
                            <card title="Sales - COGS - Payments Reports">
                                <div class="form-group">
                                    <jet-label>From*:</jet-label><br>
                                    <Calendar v-model="saleReportForm.from" :showIcon="true" class="m-w-100"
                                        dateFormat="mm/dd/yy" />
                                    <jet-input-error :message="saleReportForm.errors.from" class="mt-2" />
                                </div>
                                <div class="form-group">
                                    <jet-label>To*:</jet-label><br>
                                    <Calendar v-model="saleReportForm.to" :showIcon="true" class="m-w-100"
                                        dateFormat="mm/dd/yy" />
                                    <jet-input-error :message="saleReportForm.errors.to" class="mt-2" />
                                </div>

                                <jet-button @click="submitSaleReport()" :disabled="saleReportForm.processing"
                                    :loading="saleReportForm.processing">Generate</jet-button>
                            </card>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-12">
                            <card title="Aging Accounts Receivable">

                                <div class="form-group">
                                    <jet-label>To*:</jet-label><br>
                                    <Calendar v-model="agingARForm.to" :showIcon="true" class="m-w-100"
                                        dateFormat="mm/dd/yy" />
                                    <jet-input-error :message="agingARForm.errors.to" class="mt-2" />
                                </div>

                                <jet-button @click="submitAgingARReport()">Generate</jet-button>
                            </card>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-12">
                            <card title="Inventory Summary">
                                <jet-button @click="submitInventorySummaryReport()">Generate</jet-button>
                            </card>
                        </div>
                    </div>

                    <table class="table table-bordered" v-if="reports">
                        <tbody>
                            <tr>
                                <th></th>
                            </tr>
                            <tr v-for="report in reports">
                                <td><a :href="route('accounting.download', { filename: report })">{{ report }}</a></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="col-lg-9 col-md-6">
                    <card headerStyle="info" title="Export histories">
                        <DataTable ref="historiesList" v-if="histories && histories.length > 0" :paginator="true" :rows="10"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            removableSort paginatorPosition="both"
                            currentPageReportTemplate="({first} - {last} of {totalRecords})"
                            :rowsPerPageOptions="[10, 20, 50, 100]" sortField="id" :sortOrder="-1"
                            class="p-datatable-sm admin-datatable" tableClass="border-left border-right border-bottom"
                            :value="histories" dataKey="id">
                            <Column field="id" header="#" headerStyle="width: 3em"></Column>
                            <Column header="Requested At" field="created_at" headerStyle="width: 10em">
                                <template #body="slotProps">
                                    {{ moment(slotProps.data.created_at).format('M/D/YY') }}
                                </template>
                            </Column>
                            <Column field="type" header="Request Type">
                                <template #body="slotProps">
                                    {{ historyTypes[slotProps.data.type] || slotProps.data.type }}
                                </template>
                            </Column>
                            <Column field="type" header="Period Time">
                                <template #body="slotProps">
                                    <div>
                                        <div v-if="!!slotProps.data.params.from" class="mt-1">From: {{
                                            moment(slotProps.data.params.from).format('M/D/YY') }}</div>
                                        <div v-if="!!slotProps.data.params.to" class="mt-1">To: {{
                                            moment(slotProps.data.params.to).format('M/D/YY') }}</div>
                                    </div>
                                </template>
                            </Column>
                            <Column header="Status">
                                <template #body="slotProps">
                                    {{ slotProps.data.status }}
                                    <i class="fas fa-exclamation text-danger" v-if="slotProps.data.status === 'failed'"></i>
                                    <i class="fas fa-check text-success" v-if="slotProps.data.status === 'done'"></i>
                                </template>
                            </Column>
                            <Column class="button-container text-nowrap" :exportable="false">
                                <template #body="slotProps">
                                    <a class="btn-short text-nowrap m-1" btnStyle="dark" title="Download result"
                                        v-if="slotProps.data.output_file_link" :href="slotProps.data.output_file_link">
                                        <i class="fas fa-download"></i> Download result
                                    </a>
                                </template>
                            </Column>
                        </DataTable>
                        <h6 v-else>No Reports </h6>
                    </card>
                </div>
            </div>
        </template>
    </layout>
</template>

<script>
import AppHead from '@/Shared/AppHead'
import Layout from '@/Shared/Admin/Layout'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label'
import JetButton from '@/Jetstream/Button'
import moment from 'moment'
import FlashMessages from '@/Shared/FlashMessages'
import Card from '@/Shared/Card'
import Calendar from 'primevue/calendar'
import { historyTypes } from '@/Consts'

export default {
    components: {
        AppHead,
        Layout,
        FlashMessages,
        JetInputError,
        JetLabel,
        JetButton,
        Card,
        Calendar,
    },
    props: {
        reports: Array,
        histories: Array,
    },
    data() {
        return {
            saleReportForm: this.$inertia.form({
                // @reportStart should default to April 1 of the current year
                from: moment().date(1).month('April').isSameOrAfter(moment()) ? 
                    moment().date(1).month('April').subtract(1, 'years').format('M/D/Y') : 
                    moment().date(1).month('April').format('M/D/Y'),
                // @reportEnd should default to the last day of previous month
                to: moment().startOf('month').subtract(1, 'hour').format('M/D/Y'),
            }),
            agingARForm: this.$inertia.form({
                // Default the date to the last day of the previous month
                to: moment().startOf('month').subtract(1, 'hour').format('M/D/Y'),
            }),
            inventorySummaryForm: this.$inertia.form({
            }),
            form: this.$inertia.form({
                from: null,
                to: null,
            }),
            historyTypes,
            checkHistoriesInterval: undefined,
        }
    },
    created: function () {
        this.moment = moment;
    },
    mounted() {
        this.checkHistoriesInterval = setInterval(() => {
            this.getHistories()
        }, 30000)
    },
    unmounted() {
        this.checkHistoriesInterval && clearInterval(this.checkHistoriesInterval)
    },
    methods: {
        importStripePayments() {
            this.form.post(route('accounting.stripeImport'));
        },
        submitSaleReport() {
            this.saleReportForm.from = moment.utc(this.saleReportForm.from).startOf('day');
            this.saleReportForm.to = moment.utc(this.saleReportForm.to).endOf('day');
            this.saleReportForm.post(route('accounting.generate', 'sales'),);
            this.saleReportForm.from = null;
            this.saleReportForm.to = null;
        },
        submitAgingARReport() {
            this.agingARForm.to = moment.utc(this.agingARForm.to).endOf('day');
            this.agingARForm.post(route('accounting.generate', 'inventoryaging'));
            this.agingARForm.to = null;
        },
        submitInventorySummaryReport() {
            this.inventorySummaryForm.post(route('accounting.generate', 'inventory_summary'));
        },
        generate(type) {
            if (type === 'sales') {
                this.form.to = moment(this.form.to).endOf('month').format('Y-MM-DD')
            }
            if (type === 'sales-order') {
                // skip time for use system default timezone
                this.form.from = moment(this.form.from).format('Y-MM-DD')
                this.form.to = moment(this.form.to).format('Y-MM-DD')
            }
            this.form.post(route('accounting.generate', type));
            this.form.to = null
            this.form.from = null
        },
        getHistories() {
            this.$inertia.reload({
                only: ['histories'],
            })
        }
    },
}

</script>
